import { Controller } from "@hotwired/stimulus";

// Front-page newsletter signup. Posts the address in place and reports back
// under the field, rather than reloading the index and dropping the customer
// back at the top of a long page to work out whether anything happened.
//
// Same fetch + JSON contract every other rebuilt form here uses (see
// step2_submit_controller.js): explicit X-CSRF-Token from csrf_meta_tag,
// credentials same-origin, and the server answers { status, message } with
// the message already translated for this gate's locale -- nothing user
// facing is written in this file.
//
// The form posts perfectly well without this controller (it is a real
// form_tag to a real route); this only upgrades it to answer without a
// navigation.
export default class extends Controller {
  static targets = ["email", "button", "spinner", "message"];

  async submit(event) {
    event.preventDefault();
    if (this.submitting) return;

    this.hideMessage();
    this.submitting = true;
    this.setBusy(true);

    let data;
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const response = await fetch(this.element.action, {
        method: "POST",
        body: new FormData(this.element),
        headers: { Accept: "application/json", ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}) },
        credentials: "same-origin"
      });
      data = await response.json();
    } catch (e) {
      data = null;
    }

    this.submitting = false;
    this.setBusy(false);

    if (data && data.status === "ok") {
      this.showMessage(data.message, true);
      // Clearing the field is the other half of "this worked": an address
      // still sitting there reads as not yet sent.
      if (this.hasEmailTarget) this.emailTarget.value = "";
      return;
    }

    // A dropped request has no message of its own to show. Falling back to
    // the server's own generic wording would mean shipping a second copy of
    // it in here, untranslated -- so reuse the one the page already has.
    this.showMessage((data && data.message) || this.messageTarget.dataset.genericError || "", false);
  }

  setBusy(busy) {
    if (this.hasButtonTarget) this.buttonTarget.disabled = busy;
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.toggle("hidden", !busy);
  }

  showMessage(text, ok) {
    if (!this.hasMessageTarget || !text) return;
    this.messageTarget.textContent = text;
    this.messageTarget.classList.remove("hidden");
    this.messageTarget.classList.toggle("text-green-700", ok);
    this.messageTarget.classList.toggle("text-danger", !ok);
  }

  hideMessage() {
    if (!this.hasMessageTarget) return;
    this.messageTarget.classList.add("hidden");
  }
}
